import { sql } from "drizzle-orm";
import { boolean, check, pgTable as table, text, timestamp } from "drizzle-orm/pg-core";

export const user = table(
  "user",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull(),
    email: text("email").notNull().unique(),
    emailVerified: boolean("email_verified").notNull().default(false),
    image: text("image"),
    /**
     * The §5 Nymbl Staff role (ADMIN / RM / SA / ACCT / FIN). Better Auth's admin-plugin
     * role IS the app role — there is no second column.
     *
     * The default is 'SA', matching `admin({ defaultRole: "SA" })` in @/lib/auth. It used
     * to be the literal 'user', which no longer means anything: `isNymblRole` rejects it,
     * so any row inserted outside Better Auth (a script, a fixture, a manual INSERT) got a
     * role that 401s on every guarded route.
     */
    role: text("role").notNull().default("SA"),
    /**
     * Where `role` came from, which is what makes a manual override stick:
     *   'manual'  — an admin set it by hand from Settings → Users. The automatic mapping
     *               MUST NOT overwrite it on a later sign-in (AC8).
     *   'auto'    — a role-mapping rule set it; it is re-evaluated on each sign-in.
     *   'default' — nothing matched, so the user was born at the default role.
     *
     * The column default is 'manual' deliberately: every user that predates this column
     * keeps whatever role they hold today and is never silently remapped. New users get
     * 'auto' or 'default' written explicitly by the sign-in hook.
     */
    roleSource: text("role_source").notNull().default("manual"),
    banned: boolean("banned").default(false),
    banReason: text("ban_reason"),
    banExpires: timestamp("ban_expires", { withTimezone: true, mode: "date" }),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "date" }).notNull(),
  },
  (t) => [check("user_role_source_valid", sql`${t.roleSource} IN ('auto', 'manual', 'default')`)],
);

export const session = table("session", {
  id: text("id").primaryKey(),
  expiresAt: timestamp("expires_at", { withTimezone: true, mode: "date" }).notNull(),
  token: text("token").notNull().unique(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  userId: text("user_id")
    .notNull()
    .references(() => user.id, { onDelete: "cascade" }),
  impersonatedBy: text("impersonated_by"),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true, mode: "date" }).notNull(),
});

export const account = table("account", {
  id: text("id").primaryKey(),
  accountId: text("account_id").notNull(),
  providerId: text("provider_id").notNull(),
  userId: text("user_id")
    .notNull()
    .references(() => user.id, { onDelete: "cascade" }),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  idToken: text("id_token"),
  accessTokenExpiresAt: timestamp("access_token_expires_at", { withTimezone: true, mode: "date" }),
  refreshTokenExpiresAt: timestamp("refresh_token_expires_at", {
    withTimezone: true,
    mode: "date",
  }),
  scope: text("scope"),
  password: text("password"),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true, mode: "date" }).notNull(),
});

export const verification = table("verification", {
  id: text("id").primaryKey(),
  identifier: text("identifier").notNull(),
  value: text("value").notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true, mode: "date" }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }),
  updatedAt: timestamp("updated_at", { withTimezone: true, mode: "date" }),
});
